import React, { useEffect, useMemo, useRef, useState } from 'react';
import './WordleGame.css';
import './McqGame.css';
import { PointsStarIcon } from './WordleGame';
import { s3PublicUrl } from '../../utils/s3PublicUrl';
import DayQuestionPanel from './DayQuestionPanel';
import { useSelector } from 'react-redux';
import { demoGatePopupAndRedirect, isUserDemoBypass } from '../../utils/demoGate';

function normalizeString(s) {
  return String(s ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

export default function McqGame({
  questions,
  rules,
  badgeUrl,
  badgeTitle = '',
  questionImage,
  didYouKnowImage,
  correctPoints,
  dayBonusPoints,
  pointsIconUrl,
  badgeFallbackLabel = 'Day 4',
  onComplete,
}) {
  const user = useSelector((state) => state.auth?.user);
  const demoPromoScheduledRef = useRef(false);
  const list = useMemo(() => (Array.isArray(questions) ? questions : []), [questions]);
  const rulesList = Array.isArray(rules) ? rules : [];

  const [badgeImageFailed, setBadgeImageFailed] = useState(false);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [earnedPoints, setEarnedPoints] = useState(0);
  const [submittedTotalPoints, setSubmittedTotalPoints] = useState(0);
  const [showRules, setShowRules] = useState(false);
  const [showDidYouKnow, setShowDidYouKnow] = useState(false);
  const [didYouKnowText, setDidYouKnowText] = useState('');
  const [revealAnswer, setRevealAnswer] = useState('');
  const [wasCorrect, setWasCorrect] = useState(false);
  const [allCorrectSoFar, setAllCorrectSoFar] = useState(true);
  const [lastWasCorrect, setLastWasCorrect] = useState(false);
  const [pointsToast, setPointsToast] = useState(null);
  const completeSentRef = useRef(false);
  const pickedRef = useRef(false);

  const pointsAward = useMemo(() => {
    const n = Number(correctPoints);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [correctPoints]);

  const dayBonusAward = useMemo(() => {
    const n = Number(dayBonusPoints);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }, [dayBonusPoints]);

  useEffect(() => {
    setBadgeImageFailed(false);
  }, [badgeUrl]);

  const current = list[questionIndex];
  const opts = Array.isArray(current?.options) ? current.options : [];
  const answer = String(current?.answer ?? '');
  const didYouKnow = String(current?.did_you_know ?? '');
  const questionImageUrl = s3PublicUrl(questionImage);
  const didYouKnowImageUrl = s3PublicUrl(didYouKnowImage);

  const handlePick = (opt) => {
    if (!current || showDidYouKnow) return;
    if (pickedRef.current) return;
    pickedRef.current = true;
    if (isUserDemoBypass(user)) {
      if (!demoPromoScheduledRef.current) {
        demoPromoScheduledRef.current = true;
        demoGatePopupAndRedirect();
      }
      return;
    }
    const correct = normalizeString(opt) === normalizeString(answer);
    setLastWasCorrect(correct);
    if (!correct) setAllCorrectSoFar(false);
    const nextTotalPoints = earnedPoints + (correct ? pointsAward : 0);
    setWasCorrect(correct);
    setRevealAnswer(correct ? '' : answer);
    setDidYouKnowText(didYouKnow);
    setSubmittedTotalPoints(nextTotalPoints);
    if (correct) setEarnedPoints(nextTotalPoints);
    setShowDidYouKnow(true);
  };

  const handleCloseDyk = () => {
    setShowDidYouKnow(false);
    pickedRef.current = false;
    const next = questionIndex + 1;
    if (next >= list.length) {
      if (typeof onComplete === 'function') {
        const allCorrect = Boolean(allCorrectSoFar && lastWasCorrect && list.length > 0);
        const finalPoints = submittedTotalPoints + (allCorrect ? dayBonusAward : 0);
        if (allCorrect && dayBonusAward > 0) {
          setEarnedPoints(finalPoints);
          setPointsToast(`+${dayBonusAward}`);
          window.setTimeout(() => setPointsToast(null), 2600);
        }
        if (!completeSentRef.current) {
          completeSentRef.current = true;
          onComplete({ earnedPoints: finalPoints, answer: 'completed', allCorrect });
        }
      }
      return;
    }
    setSubmittedTotalPoints(earnedPoints);
    setQuestionIndex(next);
  };

  if (!list.length) {
    return (
      <p className="wordle-notification" style={{ textAlign: 'center' }}>
        No questions configured for this stage.
      </p>
    );
  }

  return (
    <div className="wordle-shell mcq-shell">
      <div className="wordle-toolbar-strip">
        <div className="wordle-toolbar-end">
          {rulesList.length > 0 ? (
            <button
              type="button"
              className="wordle-icon-btn wordle-icon-rules"
              onClick={() => setShowRules(true)}
              aria-label="How to play"
              title="How to play"
            >
              ?
            </button>
          ) : null}
          <div className="wordle-points-group" title="Points">
            <PointsStarIcon pointsIconUrl={pointsIconUrl} />
            <span className="wordle-points-value">{earnedPoints}</span>
            {pointsToast ? <span className="wordle-points-toast">{pointsToast}</span> : null}
          </div>
        </div>
      </div>

      <div className="wordle-root">
        <div className="wordle-badge-header">
          <div className="wordle-badge-header__logo">
            {badgeUrl && !badgeImageFailed ? (
              <img
                src={badgeUrl}
                alt=""
                className="wordle-badge-img"
                onError={() => setBadgeImageFailed(true)}
              />
            ) : (
              <div className="wordle-badge-fallback" aria-hidden>
                {badgeFallbackLabel}
              </div>
            )}
          </div>
          <div className="wordle-badge-header__title">
            <span className="wordle-badge-title">
              {String(badgeTitle || '').trim() || badgeFallbackLabel}
            </span>
          </div>
        </div>

        <div className="mcq-stage">
          {questionImageUrl ? (
            <DayQuestionPanel
              imageUrl={questionImageUrl}
              question={String(current?.question ?? '')}
              className="mcq-question-panel"
            />
          ) : (
            <div className="mcq-question-bubble" role="region" aria-label="Question">
              <p className="mcq-question-bubble__text">{String(current?.question ?? '')}</p>
            </div>
          )}
          <div className="mcq-options" role="list">
            {opts.slice(0, 4).map((opt) => (
              <button
                key={opt}
                type="button"
                className="mcq-option"
                onClick={() => handlePick(opt)}
                disabled={showDidYouKnow}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        {showRules && rulesList.length > 0 && (
          <div
            className="wordle-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mcq-rules-title"
          >
            <div className="wordle-modal">
              <h2 id="mcq-rules-title">How to play</h2>
              <ul className="wordle-rules-list">
                {rulesList.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
              <div className="wordle-modal-actions">
                <button type="button" onClick={() => setShowRules(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showDidYouKnow && (
          <div
            className="wordle-modal-overlay wordle-modal-overlay--dyk"
            role="dialog"
            aria-modal="true"
            aria-label="Answer details"
          >
            <div className="wordle-modal wordle-modal--dyk">
              {didYouKnowImageUrl ? (
                <img src={didYouKnowImageUrl} alt="" className="dyk-banner-image" />
              ) : (
                <div className="dyk-banner-fallback">Did you know?</div>
              )}

              <div className="dyk-body-box">
                {!wasCorrect && revealAnswer ? (
                  <p className="mcq-reveal dyk-reveal">
                  Correct Answer: <strong>{revealAnswer}</strong>
                  </p>
                ) : null}
                {didYouKnowText ? <p className="dyk-text">{didYouKnowText}</p> : null}
              </div>

              <div className="wordle-modal-actions wordle-modal-actions--center">
                <button type="button" onClick={handleCloseDyk}>
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

